/**
 * SitePicker: choose one or more sites of the current network (plan
 * docs/plans/lms-plan.md 8.2 "everyone at one or more sites", 4.9; WP-43;
 * reused by WP-45 and WP-46).
 *
 * Props (stable):
 *   value      string[]                 the chosen site ids
 *   onChange   (ids: string[]) => void
 *   label?     string                   the fieldset legend (default "Sites")
 *   disabled?  boolean
 *
 * The choices are the sites the signed-in person may switch to
 * (AuthContext.availableSites) inside the current site's network (4.9:
 * nothing crosses networks). The server still holds every chosen site to the
 * admin's reach (404 "Site not found" outside it, 18.16). Each site is a
 * toggle chip (aria-pressed) with data-testid="site-option-<id>".
 */
import { useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { chipCls } from '../../../ui/styles';
import { microLabelCls } from './shared';

export interface SitePickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  disabled?: boolean;
}

export function SitePicker({ value, onChange, label = 'Sites', disabled = false }: SitePickerProps) {
  const { availableSites, currentSite } = useAuth();
  const networkId = currentSite?.network_id ?? null;

  const sites = useMemo(
    () =>
      (availableSites ?? [])
        .filter((s) => s.is_active !== false && (!networkId || s.network_id === networkId))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableSites, networkId]
  );

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <fieldset data-testid="site-picker" disabled={disabled}>
      <legend className={`${microLabelCls} mb-2`}>{label}</legend>
      {sites.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No sites are available in this network.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {sites.map((s) => {
            const on = value.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={on}
                data-testid={`site-option-${s.id}`}
                onClick={() => toggle(s.id)}
                className={chipCls(on)}
              >
                {s.name}
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

export default SitePicker;
